import React, { useState, useEffect } from 'react';
import { Filter } from '../Filter/Filter';
import { PopulationSlider } from '../PopulationSlider/PopulationSlider';
import './FilterMenu.css';
import Select from 'react-select';
import { policyTypeOptions, policySubtypeOptions } from './policyTypes';

// Re-export for backwards compatibility
export { policyTypeOptions, policySubtypeOptions };

const getAllGroupedSubtypes = (policySubtypeOptions) => {
    return Object.entries(policySubtypeOptions).map(([parent, subtypes]) => ({
        label: parent,
        options: subtypes
    }));
};

const FilterMenu = ({ 
    onFilterChange,
    filters = null, // Current filter state from parent
    titleColors = {
        policyType: '#f27929',
        policySubtype: '#f27929',
        population: '#f27929',
        communityType: '#f27929'
    },
    populationColorPalette = null, // Optional custom color palette for population ranges
    clearFiltersTrigger = 0 // Prop to trigger clearing filters externally
}) => {
    // State for tracking which filters are active - initialize from filters prop
    const [activeFilters, setActiveFilters] = useState(filters || {
        policyTypes: [],
        policySubtypes: [],
        communitySize: [],
        geographyType: [],
        cityPopulation: null,
        countyPopulation: null
    });

    // State for triggering slider resets
    const [resetTrigger, setResetTrigger] = useState(0);

    // Effect to sync internal state with external filters prop
    // Only update if filters actually changed to prevent infinite loops
    useEffect(() => {
        if (filters && JSON.stringify(filters) !== JSON.stringify(activeFilters)) {
            setActiveFilters(filters);
        }
    }, [filters, activeFilters]);

    // Effect to handle external clear filters trigger
    useEffect(() => {
        if (clearFiltersTrigger > 0) {
            const emptyFilters = {
                policyTypes: [],
                policySubtypes: [],
                communitySize: [],
                geographyType: [],
                populationRange: null,
                cityPopulation: null,
                countyPopulation: null
            };
            setActiveFilters(emptyFilters);
            setPopulationValue(0);
            setResetTrigger(prev => prev + 1); // Increment to trigger slider reset
            onFilterChange(emptyFilters);
        }
    }, [clearFiltersTrigger, onFilterChange]);

    // Geography Type options
    const geographyTypeOptions = [
        { value: 'city', label: 'City or Township' },
        { value: 'county', label: 'County' }
    ];

    // State for tracking the actual numeric population value
    const [populationValue, setPopulationValue] = useState(0);

    // Add handler for opening table view
    const handleOpenTable = () => {
        // Dispatch an event that the parent component can listen to
        const event = new CustomEvent('openTableView');
        window.dispatchEvent(event);
    };

    // Handle filter changes
    const handleFilterChange = (filterId, value) => {
        setActiveFilters(prev => {
            const newFilters = { ...prev };
            const filterCategory = filterId.split('_')[0]; // e.g., 'policyTypes' from 'policyTypes_filter'
            
            if (filterCategory === 'cityPopulation') {
                // Handle the city population slider data
                if (value && (value.minThreshold !== undefined || value.maxThreshold !== undefined)) {
                    newFilters.cityPopulation = {
                        minThreshold: value.minThreshold,
                        maxThreshold: value.maxThreshold,
                        activeRanges: value.activeRanges,
                        colors: value.colors || []
                    };
                } else {
                    newFilters.cityPopulation = null;
                }
            } else if (filterCategory === 'countyPopulation') {
                // Handle the county population slider data
                if (value && (value.minThreshold !== undefined || value.maxThreshold !== undefined)) {
                    newFilters.countyPopulation = {
                        minThreshold: value.minThreshold,
                        maxThreshold: value.maxThreshold,
                        activeRanges: value.activeRanges,
                        colors: value.colors || []
                    };
                } else {
                    newFilters.countyPopulation = null;
                }
            } else if (filterCategory === 'populationSlider') {
                // Handle the old population slider data (for backward compatibility)
                if (value && value.communitySize) {
                    newFilters.communitySize = value.communitySize;
                    // Store additional data for enhanced filtering
                    newFilters.populationRange = {
                        minThreshold: value.minThreshold,
                        maxThreshold: value.maxThreshold,
                        activeRanges: value.activeRanges,
                        colors: value.colors || [] // Include colors for map styling
                    };
                } else {
                    // Clear population filters if no value
                    newFilters.communitySize = [];
                    newFilters.populationRange = null;
                }
            } else if (filterCategory === 'communitySize') {
                // Store the actual numeric value for the slider display
                const numericValue = Array.isArray(value) ? value[0] : value;
                setPopulationValue(numericValue);
                
                // Convert to category for filtering
                let categoryValue;
                if (numericValue < 1000) {
                    categoryValue = 'less_1000';
                } else if (numericValue >= 1000 && numericValue < 5000) {
                    categoryValue = '1000_5000';
                } else if (numericValue >= 5000 && numericValue < 15000) {
                    categoryValue = '5000_15000';
                } else if (numericValue >= 15000 && numericValue < 30000) {
                    categoryValue = '15000_30000';
                } else if (numericValue >= 30000 && numericValue < 50000) {
                    categoryValue = '30000_50000';
                } else {
                    categoryValue = '50000_plus';
                }
                newFilters.communitySize = [categoryValue];
            } else if (filterCategory === 'policySubtypes') {
                // Handle policy subtypes as multi-select
                newFilters[filterCategory] = Array.isArray(value) ? value : [];
            } else {
                // Handle other button/chip type filters
                const index = newFilters[filterCategory].indexOf(value);
                if (index === -1) {
                    newFilters[filterCategory] = [...newFilters[filterCategory], value];
                } else {
                    newFilters[filterCategory] = newFilters[filterCategory].filter(v => v !== value);
                }
            }
            
            // Call the parent's onFilterChange with updated filters
            onFilterChange(newFilters);
            return newFilters;
        });
    };

    // Get visible subtypes based on selected policy types
    const getVisibleSubtypes = () => {
        const subtypes = [];
        activeFilters.policyTypes.forEach(policyType => {
            if (policySubtypeOptions[policyType]) {
                subtypes.push(...policySubtypeOptions[policyType]);
            }
        });
        return subtypes;
    };

    const handleClearFilters = () => {
        const emptyFilters = {
            policyTypes: [],
            policySubtypes: [],
            communitySize: [],
            geographyType: [],
            populationRange: null,
            cityPopulation: null,
            countyPopulation: null
        };
        setActiveFilters(emptyFilters);
        setPopulationValue(0);
        setResetTrigger(prev => prev + 1); // Increment to trigger slider reset
        onFilterChange(emptyFilters);
    };

    // Handle clearing individual layers
    const handleClearLayer = (id) => {
        setActiveFilters(prev => {
            const newFilters = { ...prev };
            if (id === 'cityPopulation_filter') {
                newFilters.cityPopulation = null;
            } else if (id === 'countyPopulation_filter') {
                newFilters.countyPopulation = null;
            }
            onFilterChange(newFilters);
            return newFilters;
        });
    };

    return (
        <div className="filter-menu">
            <div className="filter-menu-content">
                {/* Policy Types Filter */}
                <Filter
                    id="policyTypes_filter"
                    title="POLICY TYPE"
                    titleColor={titleColors.policyType}
                    type="buttons"
                    options={policyTypeOptions}
                    value={activeFilters.policyTypes}
                    onChange={handleFilterChange}
                />

                {/* Policy Subtypes Filter - always visible */}
                <div className="filter-section">
                    <h4 className="filter-title" style={{ color: titleColors.policySubtype }}>POLICY SUBTYPE</h4>
                    <Select
                        isMulti
                        closeMenuOnSelect={false}
                        options={getAllGroupedSubtypes(policySubtypeOptions)}
                        value={activeFilters.policySubtypes.map(val =>
                            Object.values(policySubtypeOptions)
                                .flat()
                                .find(opt => opt.value === val)
                        )}
                        onChange={selected => {
                            handleFilterChange('policySubtypes_filter', selected ? selected.map(option => option.value) : []);
                        }}
                        placeholder="Select policy subtypes..."
                        isClearable={true}
                        classNamePrefix="select"
                        styles={{
                            groupHeading: (base) => ({
                                ...base,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                backgroundColor: '#f5f5f5',
                                margin: 0,
                                padding: '8px 12px',
                            }),
                            option: (base, state) => ({
                                ...base,
                                fontSize: '13px',
                                color: '#666',
                                paddingLeft: '24px',
                                backgroundColor: state.isSelected ? '#e6e6e6' : state.isFocused ? '#f0f0f0' : 'white',
                                ':active': {
                                    backgroundColor: '#e6e6e6'
                                }
                            })
                        }}
                    />
                </div>

                {/* City Population Slider Filter */}
                <PopulationSlider
                    id="cityPopulation_filter"
                    title="CITY POPULATION"
                    titleColor={titleColors.population}
                    minValue={80}
                    maxValue={430000}
                    initialMin={80}
                    initialMax={430000}
                    value={activeFilters.cityPopulation}
                    onChange={handleFilterChange}
                    step={1000}
                    colorPalette={populationColorPalette}
                    resetTrigger={resetTrigger}
                    onClear={handleClearLayer}
                />

                {/* County Population Slider Filter */}
                <PopulationSlider
                    id="countyPopulation_filter"
                    title="COUNTY POPULATION"
                    titleColor={titleColors.population}
                    minValue={100000}
                    maxValue={1400000}
                    initialMin={100000}
                    initialMax={1400000}
                    value={activeFilters.countyPopulation}
                    onChange={handleFilterChange}
                    step={1000}
                    colorPalette={populationColorPalette}
                    resetTrigger={resetTrigger}
                    onClear={handleClearLayer}
                />

                {/* Geography Type Filter */}
                <Filter
                    id="geographyType_filter"
                    title="COMMUNITY TYPE"
                    titleColor={titleColors.communityType}
                    type="buttons"
                    options={geographyTypeOptions}
                    value={activeFilters.geographyType}
                    onChange={handleFilterChange}
                />

                {/* Open Table Button */}
                <div className="filter-actions" style={{ borderTop: 'none', marginTop: '8px', marginBottom: '32px' }}>
                    <button 
                        className="open-table-button"
                        onClick={handleOpenTable}
                        style={{
                            backgroundColor: '#091732',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            width: '100%',
                            fontWeight: '500',
                            fontSize: '14px',
                            transition: 'background-color 0.2s ease'
                        }}
                    >
                        Open Table to Learn More
                    </button>
                </div>
            </div>

            {/* Fixed Footer */}
            <div className="filter-menu-footer">
                <div className="filter-actions" style={{ margin: 0, padding: 0, border: 'none' }}>
                    <button 
                        className="clear-filters"
                        onClick={handleClearFilters}
                    >
                        Clear Filters
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FilterMenu;
